import { ThemeProvider } from '@/common/contexts/ContextProvider';
import { getEnvDataFromContext } from '@/common/helpers/requestHelpers';
import { AWSRumProps, getAwsRumConfig, useAWSRum } from '@/common/monitoring/awsrum';
import { IntlProviderWrapper } from '@/common/providers/IntlProvider';
import { Env } from '@/common/types';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import '@/styles/globals.css';
import { ConsentManagerScripts } from '@eventbrite/webapp-data';
import { IncomingMessage } from 'http';
import type { AppContext, AppProps } from 'next/app';
import NextApp from 'next/app';
import Head from 'next/head';
import { getLocaleCode, getLocaleMessages } from '../../i18n/locale';

interface MLPAppProps extends AppProps {
  awsRumConfig: AWSRumProps;
  locale: string;
  messages: Record<string, string>;
  env: Env;
}

export default function App({
  Component,
  pageProps,
  awsRumConfig,
  locale,
  messages,
  env,
}: MLPAppProps) {
  useAWSRum(awsRumConfig);

  return (
    <>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Head>
      <ThemeProvider>
        <IntlProviderWrapper messages={messages} locale={locale}>
          <ErrorBoundary env={env} caller="App">
            <Component {...pageProps} />
          </ErrorBoundary>
          <ConsentManagerScripts />
        </IntlProviderWrapper>
      </ThemeProvider>
    </>
  );
}

App.getInitialProps = async (context: AppContext) => {
  const appProps = await NextApp.getInitialProps(context);
  const locale = await getLocaleCode(context.ctx.req as IncomingMessage);
  const messages = await getLocaleMessages(context.ctx.req as IncomingMessage);
  const env = getEnvDataFromContext(context.ctx);

  return {
    ...appProps,
    awsRumConfig: getAwsRumConfig(),
    locale,
    messages,
    env,
  };
};
